// src/components/Inquiry/items/ProductLineRow.tsx
//
// A line for a manufactured product - it has a model, so it has a preview, a
// material, dimensions and files. One component in two modes: reading a saved
// inquiry, and picking lines for one that is still a Draft. They differ by a
// tick box and by what the Files column offers, and by nothing else - the
// editable table used to show the product type where the read-only one showed
// the material, and neither could be fixed without the other drifting.
import { FunctionComponent, ReactNode, useContext, useEffect, useState } from "react";
import { Checkbox, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { DoDisturb } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";
import AppStateContext from "contexts/AppStateContext";
import PreviewButton from "components/Generic/PreviewButton";
import ThreadDisplay from "components/OrderView/ThreadDisplay";
import { dangerColor } from "assets/colors";
import {
  checkColorStep,
  fetchPDFFile,
  fetchSTEPFile,
  fetchThreadFile,
} from "util/FileDownload";
import {
  ManufacturedProductDetailsType,
  ProductTypeEnum,
} from "types/products/ProductCommandsType";
import { InquiryTypeEnum } from "types/InquiryTypes";
import {
  CELL_PRIMARY,
  CELL_SECONDARY,
  SP_1,
  STACKED_CELL,
} from "components/Dashboard/dashboardTable";
import { ReactComponent as CommentIcon } from "../../../assets/svg/commentFile.svg";
import { ReactComponent as ThreadsIcon } from "../../../assets/svg/threadFile.svg";
import BlueprintMenu, { BlueprintDetails } from "./BlueprintMenu";
import {
  EMPTY,
  INQUIRY_ITEM_COLUMNS,
  SELECT_COLUMN,
  itemCellProps,
  showsQuantity,
} from "./inquiryItemColumns";
import { InquiryLine, formatLineQuantity } from "./inquiryLine";
import SpecCell from "./SpecCell";

type PropsType = {
  line: InquiryLine;
  /**
   * The inquiry the line belongs to, which decides whether the part's model
   * and drawing belong in the Files column. Optional: a caller that does not
   * know the type gets the files, which is what every caller did before.
   */
  inquiryType?: InquiryTypeEnum;
} & (
  /** Cells after Qty - the goods-in counts, once there are any. */
  | { mode: "read"; trailing?: ReactNode }
  | { mode: "edit"; selected: boolean; onToggle: () => void }
);

// Purchasing's rule for what the supplier gets of the part, mirrored from
// inquiry_file_attachments in backend/services/Email/EmailService.py: a
// finisher, a tool supplier and a raw material merchant are all quoted from
// the inquiry PDF alone, because none of them is making the part. Offering a
// drawing here that the email will not carry is worse than not offering it -
// the buyer uploads one and believes it went out.
//
// The one material inquiry that does get the part's files is processed raw
// material, and there it is the OTHER line pointing at a part that carries
// them: those lines are OtherLineRow's, never this row's, so a part row on
// either material type is withheld here, as the backend does.
const PART_FILES_WITHHELD: readonly InquiryTypeEnum[] = [
  InquiryTypeEnum.FINISH,
  InquiryTypeEnum.MATERIAL,
  InquiryTypeEnum.PROCESSED_RAW_MATERIAL,
  InquiryTypeEnum.TOOL,
];

export const partFilesApply = (type: InquiryTypeEnum | undefined): boolean =>
  type === undefined || !PART_FILES_WITHHELD.includes(type);

// The icons sat in four hand-spaced spans, each with its own paddingBottom and
// paddingLeft. They are one cluster and are spaced as one.
const ICON_CLUSTER = {
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: SP_1,
};

const ProductLineRow: FunctionComponent<PropsType> = (props) => {
  const { line } = props;
  // InquiryItemsTable routes product-less lines to OtherLineRow, so a product
  // is guaranteed by the time this row renders.
  const product = line.product!;
  const { token } = useContext(AppStateContext);
  // The order, quote and inquiry endpoints all serialise the product's details
  // inline (ProductVM.details), so there is nothing to fetch per row - this
  // used to be one apiGetProduct per line on mount. It is state only because
  // uploading a drawing replaces it.
  const [details, setDetails] = useState(
    product.details as ManufacturedProductDetailsType | undefined
  );
  const [colorStepExists, setColorStepExists] = useState(false);

  useEffect(() => {
    setDetails(product.details as ManufacturedProductDetailsType | undefined);
  }, [product.details]);

  useEffect(() => {
    if (!details?.modelId) return;
    checkColorStep(token, details.modelId).then((res) =>
      setColorStepExists(res === 200)
    );
  }, [token, details?.modelId]);

  const partFiles = partFilesApply(props.inquiryType);
  const hasBlueprint =
    partFiles &&
    (product.productType === ProductTypeEnum.CNC ||
      product.productType === ProductTypeEnum.SHEET);
  const blueprint = (details as BlueprintDetails | undefined)?.blueprint;

  const material = details?.customMaterial
    ? details.customMaterial.name
    : details?.standardMaterial
    ? details.standardMaterial.name
    : details?.material ?? EMPTY;

  const dimensions = details?.model
    ? `${details.model.dimX?.toFixed(1) ?? "?"} x ` +
      `${details.model.dimY?.toFixed(1) ?? "?"} x ` +
      `${details.model.dimZ?.toFixed(1) ?? "?"}`
    : EMPTY;

  return (
    <TableRow
      hover={props.mode === "edit"}
      selected={props.mode === "edit" && props.selected}
      onClick={props.mode === "edit" ? props.onToggle : undefined}
      sx={props.mode === "edit" ? { cursor: "pointer" } : undefined}
    >
      {props.mode === "edit" && (
        <TableCell {...itemCellProps(SELECT_COLUMN)} padding="checkbox">
          <Checkbox color="primary" checked={props.selected} />
        </TableCell>
      )}

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.item)}>
        <span style={{ display: "flex", flexDirection: "row", minWidth: 0 }}>
          {details ? (
            <PreviewButton
              productId={details.productId}
              modelId={details.modelId}
              size={"small"}
            />
          ) : (
            "Intet preview"
          )}
          <span style={STACKED_CELL}>
            <Typography sx={CELL_PRIMARY}>#{product.id}</Typography>
            <Typography sx={{ ...CELL_SECONDARY, whiteSpace: "nowrap" }}>
              {product.name}
            </Typography>
          </span>
        </span>
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.spec)}>
        <SpecCell lines={[material, dimensions]} />
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.files)}>
        {props.mode === "edit" ? (
          // While the inquiry is being written the drawing is still being
          // attached, so this column is the place to attach it. Once it is
          // sent, the same column is where the files are read from.
          <span style={ICON_CLUSTER}>
            {hasBlueprint ? (
              <BlueprintMenu
                productId={product.id}
                details={details as BlueprintDetails | undefined}
                onDetailsChange={setDetails}
              />
            ) : (
              <Typography sx={{ ...CELL_PRIMARY, color: "var(--black75)" }}>
                {EMPTY}
              </Typography>
            )}
          </span>
        ) : (
          <span style={ICON_CLUSTER}>
            {partFiles && (
              <span style={{ display: "flex", alignItems: "center" }}>
                {details ? (
                  <p
                    className={"downloadLink"}
                    onClick={() =>
                      colorStepExists
                        ? fetchThreadFile(token, product.name, details.modelId)
                        : fetchSTEPFile(token, product.name, details.modelId)
                    }
                  >
                    <ThreeDRotationIcon />
                  </p>
                ) : (
                  <DoDisturb sx={{ color: dangerColor[0] }} />
                )}
              </span>
            )}
            {partFiles && (
              <span style={{ display: "flex", alignItems: "center" }}>
                {blueprint ? (
                  <p
                    className={"downloadLink"}
                    onClick={() => fetchPDFFile(token, product.name, product.id)}
                  >
                    <PictureAsPdfIcon />
                  </p>
                ) : (
                  <DoDisturb sx={{ color: dangerColor[0] }} />
                )}
              </span>
            )}
            <span style={{ display: "flex", alignItems: "center" }}>
              {details?.comment ? (
                <Tooltip title={details.comment}>
                  <CommentIcon style={{ fill: "var(--mainYellow)" }} />
                </Tooltip>
              ) : (
                <Tooltip title={"No comment"}>
                  <CommentIcon style={{ fill: "#EBEBEB" }} />
                </Tooltip>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center" }}>
              {details?.threads ? (
                details.threads.length > 0 ? (
                  <Tooltip
                    title={
                      <div style={{ backgroundColor: "white" }}>
                        <ThreadDisplay specs={details.threads} />
                      </div>
                    }
                  >
                    <ThreadsIcon style={{ fill: "var(--mainYellow)" }} />
                  </Tooltip>
                ) : (
                  <ThreadsIcon style={{ fill: "#EBEBEB" }} />
                )
              ) : (
                EMPTY
              )}
            </span>
          </span>
        )}
      </TableCell>

      {showsQuantity(props.inquiryType) && (
        <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.qty)}>
          <Typography sx={CELL_PRIMARY}>{formatLineQuantity(line)}</Typography>
        </TableCell>
      )}
      {props.mode === "read" && props.trailing}
    </TableRow>
  );
};

export default ProductLineRow;
